import { type FormEvent, useEffect, useMemo, useRef } from 'react'
import { Form, useNavigate, useSearchParams } from 'react-router'
import { z } from 'zod'

import { Page } from '#app/components/page.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { Divider } from '#app/components/ui/divider.tsx'
import { type Route } from '#app/routes/mocra+/_form+/+types/label-governance.ts'
import {
	ADVERSE_EVENTS_SECTION_KEY,
	type AdverseEventsFormValues,
} from '#app/routes/mocra+/_form+/adverse-events/form.ts'
import {
	buildStepPathFromId,
	getNextFormStepId,
} from '#app/routes/mocra+/_form+/form-steps.ts'
import { useSectionForm } from '#app/routes/mocra+/_form+/hooks/use-section-form.ts'
import { useIntakeDraftState } from '#app/routes/mocra+/_form+/intake-context.tsx'
import {
	COMPLAINT_CHANNEL_OPTIONS,
	COMPLAINT_CHANNEL_VALUE_SET,
} from '#app/routes/mocra+/_form+/intake-options.ts'
import { AllergenManagementSection } from '#app/routes/mocra+/_form+/label-governance/allergen-management-section.tsx'
import { ArtworkControlSection } from '#app/routes/mocra+/_form+/label-governance/artwork-control-section.tsx'
import { ConsumerContactSection } from '#app/routes/mocra+/_form+/label-governance/consumer-contact-section.tsx'
import { LabelChangeWorkflowSection } from '#app/routes/mocra+/_form+/label-governance/label-change-workflow-section.tsx'
import { createRouteSectionResources } from '#app/routes/mocra+/_form+/section-resources.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'MoCRA — Label Governance' },
]

const SECTION_KEY = 'labelGovernance'
const STEP_ID = 'label-governance'
const LABEL_GOVERNANCE_RESOURCES = createRouteSectionResources({
	route: '/mocra/label-governance',
})

const COMPLAINT_CHANNEL_VALUES = COMPLAINT_CHANNEL_OPTIONS.map(
	(option) => option.value,
) as [
	(typeof COMPLAINT_CHANNEL_OPTIONS)[number]['value'],
	...(typeof COMPLAINT_CHANNEL_OPTIONS)[number]['value'][],
]

const ComplaintChannelValueSchema = z.enum(COMPLAINT_CHANNEL_VALUES)
type ComplaintChannelValue = z.infer<typeof ComplaintChannelValueSchema>

const LabelGovernanceSchema = z.object({
	consumerContactLine: z
		.string({
			required_error:
				'Provide the consumer-facing contact information printed on labels.',
		})
		.min(
			1,
			'Provide the consumer-facing contact information printed on labels.',
		),
	artworkSystem: z
		.string({
			required_error:
				'Describe the system or repository where artwork masters live.',
		})
		.min(1, 'Describe the system or repository where artwork masters live.'),
	approvalOwner: z
		.string({
			required_error:
				'Identify who approves label changes and artwork releases.',
		})
		.min(1, 'Identify who approves label changes and artwork releases.'),
	allergenTracking: z
		.string({
			required_error:
				'Explain how you track fragrance allergens and other restricted ingredients.',
		})
		.min(
			1,
			'Explain how you track fragrance allergens and other restricted ingredients.',
		),
	labelChangeProcess: z
		.string({
			required_error:
				'Summarize the workflow for requesting, reviewing, and publishing label updates.',
		})
		.min(
			1,
			'Summarize the workflow for requesting, reviewing, and publishing label updates.',
		),
	labelIntakeChannels: z
		.array(ComplaintChannelValueSchema, {
			required_error:
				'Select the intake channels printed on consumer-facing labels.',
		})
		.min(
			1,
			'Select at least one intake channel printed on consumer-facing labels.',
		),
})

export type LabelGovernanceFormValues = z.infer<typeof LabelGovernanceSchema>

const DEFAULT_VALUES: LabelGovernanceFormValues = {
	consumerContactLine: '',
	artworkSystem: '',
	approvalOwner: '',
	allergenTracking: '',
	labelChangeProcess: '',
	labelIntakeChannels: [],
}

export default function LabelGovernance() {
	const navigate = useNavigate()
	const [searchParams] = useSearchParams()
	const intakeState = useIntakeDraftState()
	const {
		values,
		setValues,
		setFieldValue,
		updateFieldValue,
		submit,
		getFieldErrors,
		status,
		clear,
		hasStoredValue,
	} = useSectionForm({
		section: SECTION_KEY,
		schema: LabelGovernanceSchema,
		defaultValue: DEFAULT_VALUES,
		onValidSubmit: () => {
			const nextStepId = getNextFormStepId(STEP_ID)
			if (!nextStepId) return
			const nextHref = buildStepPathFromId(nextStepId, searchParams)
			void navigate(nextHref)
		},
	})

	const adverseEventsSection = intakeState.sections[
		ADVERSE_EVENTS_SECTION_KEY
	] as AdverseEventsFormValues | undefined

	const availableLabelChannelOptions = useMemo<
		ReadonlyArray<{ value: ComplaintChannelValue; label: string }>
	>(() => {
		if (!adverseEventsSection?.intakeChannels) return []
		const available = new Set<ComplaintChannelValue>()
		for (const entry of adverseEventsSection.intakeChannels) {
			if (!entry) continue
			const value = entry.channel
			if (isComplaintChannelValue(value)) {
				available.add(value)
			}
		}
		return COMPLAINT_CHANNEL_OPTIONS.filter((option) =>
			available.has(option.value),
		).map((option) => ({
			value: option.value,
			label: option.label,
		}))
	}, [adverseEventsSection])

	const didNormalizeRef = useRef(false)
	useEffect(() => {
		if (didNormalizeRef.current) return
		const normalized = normalizeLabelGovernanceDraft(values)
		if (normalized && !labelGovernanceValuesEqual(normalized, values)) {
			didNormalizeRef.current = true
			setValues(normalized)
			return
		}
		didNormalizeRef.current = true
		if (!normalized && !labelGovernanceValuesEqual(DEFAULT_VALUES, values)) {
			setValues(DEFAULT_VALUES)
		}
	}, [setValues, values])

	useEffect(() => {
		if (availableLabelChannelOptions.length === 0) {
			if (values.labelIntakeChannels.length === 0) return
			setFieldValue('labelIntakeChannels', [])
			return
		}
		const availableSet = new Set(
			availableLabelChannelOptions.map((option) => option.value),
		)
		const filtered = values.labelIntakeChannels.filter((channel) =>
			availableSet.has(channel),
		)
		if (filtered.length !== values.labelIntakeChannels.length) {
			setFieldValue('labelIntakeChannels', filtered)
		}
	}, [availableLabelChannelOptions, setFieldValue, values.labelIntakeChannels])

	const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault()
		void submit()
	}

	return (
		<Page
			title="Label Governance"
			description="Inputs here feed the §609 label-control SOP (standard operating procedure) and allergen disclosure planning."
			className="space-y-8"
		>
			<Form method="post" className="space-y-8" onSubmit={handleSubmit}>
				<Divider />
				<ConsumerContactSection
					resources={LABEL_GOVERNANCE_RESOURCES}
					value={values.consumerContactLine}
					errors={getFieldErrors('consumerContactLine')}
					selectedChannels={values.labelIntakeChannels}
					options={availableLabelChannelOptions}
					channelErrors={getFieldErrors('labelIntakeChannels')}
					onChange={(next) => setFieldValue('consumerContactLine', next)}
					onToggleChannel={(channel) =>
						updateFieldValue('labelIntakeChannels', (current) =>
							toggleIntakeChannel(toChannelValues(current), channel),
						)
					}
				/>

				<Divider />

				<ArtworkControlSection
					resources={LABEL_GOVERNANCE_RESOURCES}
					artworkSystem={values.artworkSystem}
					approvalOwner={values.approvalOwner}
					artworkSystemErrors={getFieldErrors('artworkSystem')}
					approvalOwnerErrors={getFieldErrors('approvalOwner')}
					onArtworkSystemChange={(next) => setFieldValue('artworkSystem', next)}
					onApprovalOwnerChange={(next) => setFieldValue('approvalOwner', next)}
				/>

				<Divider />

				<AllergenManagementSection
					resources={LABEL_GOVERNANCE_RESOURCES}
					value={values.allergenTracking}
					errors={getFieldErrors('allergenTracking')}
					onChange={(next) => setFieldValue('allergenTracking', next)}
				/>

				<Divider />

				<LabelChangeWorkflowSection
					resources={LABEL_GOVERNANCE_RESOURCES}
					value={values.labelChangeProcess}
					errors={getFieldErrors('labelChangeProcess')}
					onChange={(next) => setFieldValue('labelChangeProcess', next)}
				/>

				{status === 'invalid' ? (
					<p className="text-destructive text-sm">
						Check the highlighted fields before continuing.
					</p>
				) : null}

				<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
					{hasStoredValue ? (
						<Button type="button" variant="outline" onClick={() => clear()}>
							Clear answers
						</Button>
					) : (
						<span />
					)}
					<Button type="submit" className="w-full sm:w-auto">
						Continue
					</Button>
				</div>
			</Form>
		</Page>
	)
}

export function normalizeLabelGovernanceDraft(
	draft: unknown,
): LabelGovernanceFormValues | null {
	if (!draft || typeof draft !== 'object') return null
	const record = draft as Record<string, unknown>
	const toValue = (value: unknown): string => {
		if (typeof value === 'string') return value.trim()
		if (typeof value === 'number') return value.toString()
		return ''
	}
	const candidate: LabelGovernanceFormValues = {
		consumerContactLine: toValue(record.consumerContactLine),
		artworkSystem: toValue(record.artworkSystem),
		approvalOwner: toValue(record.approvalOwner),
		allergenTracking: toValue(record.allergenTracking),
		labelChangeProcess: toValue(record.labelChangeProcess),
		labelIntakeChannels: toChannelValues(record.labelIntakeChannels),
	}
	const parsed = LabelGovernanceSchema.safeParse(candidate)
	if (!parsed.success) return null
	return parsed.data
}

export function labelGovernanceValuesEqual(
	left: LabelGovernanceFormValues,
	right: LabelGovernanceFormValues,
): boolean {
	return JSON.stringify(left) === JSON.stringify(right)
}

function toggleIntakeChannel(
	current: ReadonlyArray<ComplaintChannelValue>,
	channel: ComplaintChannelValue,
): ComplaintChannelValue[] {
	if (current.includes(channel)) {
		return current.filter((value) => value !== channel)
	}
	return [...current, channel]
}

function isComplaintChannelValue(
	value: unknown,
): value is ComplaintChannelValue {
	return typeof value === 'string' && COMPLAINT_CHANNEL_VALUE_SET.has(value)
}

function toChannelValues(value: unknown): ComplaintChannelValue[] {
	if (!Array.isArray(value)) return []
	const next: ComplaintChannelValue[] = []
	for (const entry of value) {
		if (!isComplaintChannelValue(entry)) continue
		if (next.includes(entry)) continue
		next.push(entry)
	}
	return next
}
